import { z } from 'zod';
import { branchRefSchema, businessDateSchema, containerTypeSchema, fractionSchema, lineSchema, fuelMoneySchema, nonNegativeMoneySchema, notesSchema, positiveMoneySchema, productSchema, quantityLinesSchema, } from './primitives.js';
import { inventoryModesSchema, postingStatusSchema } from './inventory.contract.js';
/** GET /operator/context: everything the operator home and drawers need. */
export const operatorCategorySchema = z.object({
    id: z.string(),
    type: containerTypeSchema,
    name: z.string(),
    fraction: fractionSchema,
});
/** The shift of a product on the session branch (KAN-48; fuel.md section 4.1). */
export const shiftStatusSchema = z.object({
    product: productSchema,
    /** ACTIVE: a carried balance exists on the business date. CLOSED: the shift is closed or not yet opened. */
    state: z.enum(['ACTIVE', 'CLOSED']),
    /** When the last close ended the shift; null while ACTIVE or when the branch never closed. */
    closedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const operatorContextSchema = z.object({
    branch: branchRefSchema,
    businessDate: businessDateSchema,
    products: z.array(productSchema),
    /** Products with a carried balance on the business date; the others accept only الرصيد المدور (fuel.md 4.1). */
    openedProducts: z.array(productSchema),
    /** Shift status per allowed product (KAN-48), independent of the other product. */
    shifts: z.array(shiftStatusSchema),
    /** وضع الجرد per allowed product, for the session branch (fuel.md section 4.4). */
    inventoryModes: inventoryModesSchema,
    categories: z.array(operatorCategorySchema),
    prices: z.object({
        DIESEL: z.object({
            sale: z.object({ barrel: fuelMoneySchema, jerrycan: fuelMoneySchema }),
            purchase: z.object({ barrel: fuelMoneySchema, jerrycan: fuelMoneySchema }),
        }),
        PETROL: z.object({
            sale: z.object({ barrel: fuelMoneySchema, jerrycan: fuelMoneySchema }),
            purchase: z.object({ barrel: fuelMoneySchema, jerrycan: fuelMoneySchema }),
        }),
    }),
});
/** 1. الرصيد المدور */
export const createRevolvingBalanceSchema = z.object({
    product: productSchema,
    cashBalance: fuelMoneySchema,
    lines: quantityLinesSchema,
    /** «إلا جركل»: jerrycans deducted from the carried quantity, typed without a sign. */
    lessJerrycans: nonNegativeMoneySchema.optional(),
    notes: notesSchema,
});
/** 2. الشراء and 3. البيع النقدي share one shape; prices are editable and > 0. */
export const createQuantityTransactionSchema = z.object({
    product: productSchema,
    barrelPrice: positiveMoneySchema,
    jerrycanPrice: positiveMoneySchema,
    lines: quantityLinesSchema.refine((lines) => lines.some((l) => l.qty > 0), 'at least one quantity'),
    /** «إلا جركل»: deducted jerrycans, typed without a sign. */
    lessJerrycans: nonNegativeMoneySchema.optional(),
    notes: notesSchema,
});
export const createPurchaseSchema = createQuantityTransactionSchema;
export const createSaleSchema = createQuantityTransactionSchema;
/** 4. بيع الذمم */
export const createDebtSaleSchema = createQuantityTransactionSchema.extend({
    debtorId: z.string().min(1),
    vehicleId: z.string().min(1).optional(),
});
/** 5. سداد الذمم */
export const createDebtRepaymentSchema = z.object({
    product: productSchema,
    debtorId: z.string().min(1),
    vehicleId: z.string().min(1).optional(),
    amount: positiveMoneySchema,
    notes: notesSchema,
});
/** 6. المدفوعات and 7. المقبوضات */
export const createCashVoucherSchema = z.object({
    product: productSchema,
    amount: positiveMoneySchema,
    personName: z.string().trim().min(1).max(200),
    statement: z.string().trim().min(1).max(1000),
});
export const createPaymentSchema = createCashVoucherSchema;
export const createReceiptSchema = createCashVoucherSchema;
/** 4.1 إغلاق الشفت */
export const shiftCloseSchema = z.object({
    password: z.string().min(1).max(200),
    businessDate: businessDateSchema,
});
export const shiftCloseResultSchema = z.object({
    reference: z.string(),
    closedDate: businessDateSchema,
    businessDate: businessDateSchema,
});
/** Debtor look-up inside the operator drawers (branch debtors + shared). */
export const operatorDebtorOptionSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    name: z.string(),
    balance: fuelMoneySchema,
    debtLimit: fuelMoneySchema,
    /** INVALID_PHONE means a movement of this debtor never reaches Customers or WhatsApp. */
    linkStatus: z.enum(['LINKED', 'PENDING', 'INVALID_PHONE']),
    vehicles: z.array(z.object({ id: z.string(), plate: z.string() })),
});
export const operatorDebtorQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
});
// --- transaction lookup (fuel.md section 4.3) --------------------------------
/** GET /operator/transactions/lookup: a scanned QR text or a typed reference, and the selected product. */
export const operatorLookupQuerySchema = z.object({
    /** A reference, a PDA temporary reference or a signed sale QR (MF1, up to 2000 characters). */
    code: z.string().trim().min(1).max(2000),
    product: productSchema,
});
export const operatorLookupKindSchema = z.enum([
    'PURCHASE',
    'PDA_PURCHASE',
    'CASH_SALE',
    'PDA_SALE',
    'DEBT_SALE',
    'DEBT_REPAYMENT',
    'PAYMENT',
    'RECEIPT',
    'REVOLVING_BALANCE',
]);
export const operatorLookupStatusSchema = z.enum([
    'PENDING_PRICE',
    'PRICED',
    'PENDING_DELIVERY',
    'DELIVERED',
    'RECORDED',
    'CANCELLED',
]);
/** Why the next step is not offered; null when it is (or when there is none to offer). */
export const operatorLookupBlockSchema = z.enum([
    'WRONG_PRODUCT',
    'CANCELLED',
    'ALREADY_PRICED',
    'ALREADY_DELIVERED',
    'NO_ACTION',
    /** The status allows a step, but this user's group lacks its service (KAN-37). */
    'NOT_PERMITTED',
]);
export const operatorLookupSchema = z.object({
    id: z.string(),
    reference: z.string(),
    kind: operatorLookupKindSchema,
    product: productSchema,
    businessDate: businessDateSchema,
    createdAt: z.iso.datetime({ offset: true }),
    /** The employee who entered it (the PDA employee for a PDA purchase). */
    createdBy: z.string(),
    status: operatorLookupStatusSchema,
    /** The needed service is held and the status allows the step. */
    actions: z.object({ completePrice: z.boolean(), deliver: z.boolean() }),
    block: operatorLookupBlockSchema.nullable(),
    /** Everything below is null/empty for a cancelled transaction. */
    customer: z
        .object({
        name: z.string().nullable(),
        phone: z.string().nullable(),
        vehiclePlate: z.string().nullable(),
    })
        .nullable(),
    lines: z.array(lineSchema),
    lessJerrycans: nonNegativeMoneySchema.nullable(),
    barrelPrice: fuelMoneySchema.nullable(),
    jerrycanPrice: fuelMoneySchema.nullable(),
    total: fuelMoneySchema.nullable(),
    finalTotal: fuelMoneySchema.nullable(),
    amount: fuelMoneySchema.nullable(),
    personName: z.string().nullable(),
    statement: z.string().nullable(),
    notes: z.string().nullable(),
    pricedAt: z.iso.datetime({ offset: true }).nullable(),
    deliveredAt: z.iso.datetime({ offset: true }).nullable(),
    /**
     * حالة الترحيل of a purchase, sale, debt sale, payment or receipt (KAN-28,
     * KAN-42): pricing or delivering a pending one completes it without posting
     * it. Null for the other families.
     */
    postingStatus: postingStatusSchema.nullable(),
});
/** POST /operator/transactions/:reference/complete-price: the dashboard prices plus the selected product. */
export const operatorCompletePriceSchema = z.object({
    product: productSchema,
    barrelPrice: positiveMoneySchema,
    jerrycanPrice: positiveMoneySchema,
});
/** POST /operator/transactions/:reference/deliver: only the selected product. */
export const operatorDeliverSchema = z.object({ product: productSchema });
//# sourceMappingURL=operator.contract.js.map